import type { ZulipChannelFolder, ZulipStream } from "./types";

export type ChannelSection = {
  id: string;
  title: string;
  streams: ZulipStream[];
};

const byName = (a: ZulipStream, b: ZulipStream) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" });

// Muted channels sink to the bottom of whichever section they're in, same
// as unmuted-first ordering in Zulip's own sidebar.
function sortWithinSection(streams: ZulipStream[]): ZulipStream[] {
  return [...streams].sort((a, b) => Number(!!a.is_muted) - Number(!!b.is_muted) || byName(a, b));
}

/**
 * Mirrors Zulip's own left-sidebar grouping (web/src/stream_list_sort.ts,
 * `sort_groups`): pinned channels first, then one section per channel
 * folder (ordered by the folder's own `order`), then a catch-all section —
 * titled "Other" once any pinned/folder sections exist, "Channels"
 * otherwise. Simplified from the original: this drops the inactive-channel
 * demotion (`demote_inactive_streams`), which needs recent-activity data our
 * API subset doesn't fetch.
 */
export function groupAndSortChannels(subscriptions: ZulipStream[], folders: ZulipChannelFolder[]): ChannelSection[] {
  const folderById = new Map(folders.filter((f) => !f.is_archived).map((f) => [f.id, f]));

  const pinned: ZulipStream[] = [];
  const byFolder = new Map<number, ZulipStream[]>();
  const unfoldered: ZulipStream[] = [];

  for (const s of subscriptions) {
    if (s.is_archived) continue;
    if (s.pin_to_top) {
      pinned.push(s);
    } else if (s.folder_id != null && folderById.has(s.folder_id)) {
      const list = byFolder.get(s.folder_id) ?? [];
      list.push(s);
      byFolder.set(s.folder_id, list);
    } else {
      unfoldered.push(s);
    }
  }

  const sections: ChannelSection[] = [];
  if (pinned.length) {
    sections.push({ id: "pinned", title: "Pinned Channels", streams: sortWithinSection(pinned) });
  }

  const activeFolders = [...byFolder.keys()]
    .map((id) => folderById.get(id)!)
    .sort((a, b) => a.order - b.order);
  for (const folder of activeFolders) {
    sections.push({ id: `folder-${folder.id}`, title: folder.name, streams: sortWithinSection(byFolder.get(folder.id)!) });
  }

  sections.push({
    id: "other",
    title: sections.length > 0 ? "Other" : "Channels",
    streams: sortWithinSection(unfoldered),
  });

  return sections.filter((s) => s.streams.length > 0);
}
